/**
 * Theme Manager - Handles light/dark theme switching
 */
export class ThemeManager {
    constructor() {
        this.THEME_KEY = 'volvo-portal-theme';
        this.THEMES = {
            LIGHT: 'light',
            DARK: 'dark'
        };
        // Apply saved theme immediately to prevent flash
        this.applyTheme(this.getSavedTheme());
    }
    init() {
        // Wait for DOM to be ready before setting up event listeners
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.setupThemeToggle();
            });
        }
        else {
            this.setupThemeToggle();
        }
        // Listen for system theme changes
        this.setupSystemThemeListener();
    }
    setupThemeToggle() {
        // Desktop theme toggle
        const themeToggle = document.getElementById('theme-toggle');
        if (themeToggle) {
            // Update icon based on current theme
            this.updateThemeIcon('theme-toggle-icon');
            themeToggle.addEventListener('click', () => {
                this.toggleTheme();
            });
        }
        // Mobile theme toggle
        const mobileThemeToggle = document.getElementById('mobileThemeToggle');
        if (mobileThemeToggle) {
            // Update icon based on current theme
            this.updateThemeIcon('mobile-theme-toggle-icon');
            mobileThemeToggle.addEventListener('click', () => {
                this.toggleTheme();
            });
        }
    }
    setupSystemThemeListener() {
        // Listen for system theme changes
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
            // Only auto-switch if user hasn't manually set a preference
            if (!localStorage.getItem(this.THEME_KEY)) {
                const systemTheme = e.matches ? this.THEMES.DARK : this.THEMES.LIGHT;
                this.applyTheme(systemTheme);
                this.updateThemeIcons();
            }
        });
    }
    toggleTheme() {
        const currentTheme = this.getCurrentTheme();
        const newTheme = currentTheme === this.THEMES.LIGHT ? this.THEMES.DARK : this.THEMES.LIGHT;
        this.setTheme(newTheme);
    }
    setTheme(theme) {
        localStorage.setItem(this.THEME_KEY, theme);
        this.applyTheme(theme);
        this.updateThemeIcons();
        // Dispatch custom event for other components to listen to
        window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme } }));
    }
    applyTheme(theme) {
        document.documentElement.setAttribute('data-bs-theme', theme);
        // Update meta theme-color for mobile browsers
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            if (theme === this.THEMES.DARK) {
                metaThemeColor.setAttribute('content', '#212529');
            }
            else {
                metaThemeColor.setAttribute('content', '#0d6efd');
            }
        }
    }
    updateThemeIcons() {
        this.updateThemeIcon('theme-toggle-icon');
        this.updateThemeIcon('mobile-theme-toggle-icon');
    }
    updateThemeIcon(iconId) {
        const themeToggleIcon = document.getElementById(iconId);
        if (!themeToggleIcon)
            return;
        const currentTheme = this.getCurrentTheme();
        if (currentTheme === this.THEMES.DARK) {
            themeToggleIcon.classList.remove('bi-moon-fill');
            themeToggleIcon.classList.add('bi-sun-fill');
        }
        else {
            themeToggleIcon.classList.remove('bi-sun-fill');
            themeToggleIcon.classList.add('bi-moon-fill');
        }
    }
    getCurrentTheme() {
        const savedTheme = localStorage.getItem(this.THEME_KEY);
        if (savedTheme) {
            return savedTheme;
        }
        // Default to light theme as requested
        return this.THEMES.LIGHT;
    }
    getSavedTheme() {
        return localStorage.getItem(this.THEME_KEY) || this.THEMES.LIGHT;
    }
    getTheme() {
        return this.getCurrentTheme();
    }
    isDarkMode() {
        return this.getCurrentTheme() === this.THEMES.DARK;
    }
}
export default ThemeManager;
